import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  parseSocAiMessageFromSearch,
  readPendingAiMessage,
  storePendingAiMessage,
  stripSocAiParamsFromSearch,
} from '../ai/supportChatDeepLink';
import { useSupportChat } from '../context/SupportChatContext';

/**
 * Handles ?socAi=... deep links from Siri Shortcuts, Apple Shortcuts, or shared links.
 * Strips query params after handling so refresh does not re-send the message.
 */
export function useSupportChatDeepLink(isSignedIn: boolean): void {
  const location = useLocation();
  const navigate = useNavigate();
  const { openChatWithMessage } = useSupportChat();
  const handledSearchRef = useRef<string | null>(null);

  useEffect(() => {
    const search = location.search;
    if (!search || handledSearchRef.current === search) return;

    const message = parseSocAiMessageFromSearch(search);
    if (!message) return;

    handledSearchRef.current = search;
    const cleaned = stripSocAiParamsFromSearch(search);
    navigate({ pathname: location.pathname, search: cleaned }, { replace: true });

    if (isSignedIn) {
      openChatWithMessage(message);
    } else {
      storePendingAiMessage(message);
    }
  }, [location.pathname, location.search, navigate, isSignedIn, openChatWithMessage]);

  useEffect(() => {
    if (!isSignedIn) return;
    const stored = readPendingAiMessage();
    if (stored) openChatWithMessage(stored);
  }, [isSignedIn, openChatWithMessage]);
}
